import React, { useEffect, useState } from "react";
import { Check, RotateCcw, Save } from "lucide-react";
import { timeAgo } from "@/hooks/useAutosave";

/**
 * Tiny status indicator: "Saving…", "Saved 12s ago", or hidden when idle.
 * Re-renders every 20s while we have a saved timestamp so the relative time
 * stays fresh without forcing parent re-renders.
 */
export function AutosaveIndicator({ status, lastSavedAt }) {
  const [, force] = useState(0);
  useEffect(() => {
    if (!lastSavedAt) return;
    const t = setInterval(() => force(x => x + 1), 20000);
    return () => clearInterval(t);
  }, [lastSavedAt]);

  if (status === "saving") {
    return (
      <span className="autosave-pill autosave-saving" data-testid="autosave-status">
        <Save size={11} /> Saving…
      </span>
    );
  }
  if (status === "saved" && lastSavedAt) {
    return (
      <span className="autosave-pill autosave-saved" data-testid="autosave-status">
        <Check size={11} /> Saved {timeAgo(lastSavedAt)}
      </span>
    );
  }
  return null;
}

/**
 * Banner offered when a draft exists in storage. The parent decides whether
 * to apply (e.g. fill `setForm(draft)`) or dismiss (clears storage).
 */
export function RestoreDraftBanner({ savedAt, onResume, onDiscard }) {
  return (
    <div className="restore-banner" role="status" data-testid="restore-banner">
      <div className="restore-banner-icon" aria-hidden>
        <RotateCcw size={14} />
      </div>
      <div className="restore-banner-text">
        <strong>Resume your draft?</strong>
        <span>We saved your last edits {savedAt ? timeAgo(savedAt) : "earlier"}.</span>
      </div>
      <div className="restore-banner-actions">
        <button type="button" className="btn btn-primary restore-resume" data-testid="restore-resume" onClick={onResume}>Resume draft</button>
        <button type="button" className="btn btn-ghost restore-discard" data-testid="restore-discard" onClick={onDiscard}>Discard</button>
      </div>
    </div>
  );
}

export default AutosaveIndicator;
